package org.hs_soft.runmynesto.pages.home.sub_page.options.component.tabs.restaurant_manager.components.restaurant_project_content.design


import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.NoLiveLiterals
import kotlinx.browser.document
import org.hs_soft.runmynesto.domain.config.Theme
import org.hs_soft.runmynesto.domain.model.home.options.restaurant_manager.ObjectKinds
import org.hs_soft.runmynesto.domain.model.home.options.restaurant_manager.restaurant_project.Furniture
import org.hs_soft.runmynesto.domain.model.home.options.restaurant_manager.restaurant_project.Room
import org.hs_soft.runmynesto.domain.util.jsObject
import org.hs_soft.runmynesto.modules.Konva
import org.hs_soft.runmynesto.modules.React
import org.hs_soft.runmynesto.modules.ReactDom
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag
import org.w3c.xhr.XMLHttpRequest
import kotlin.math.abs
import kotlin.math.roundToInt

// Global variables to persist grid layer across renders
private var persistentGridLayer: dynamic = null
private var lastUnitSize: Int = -1

@Composable
@NoLiveLiterals
fun RenderKonvaStage(
    rootRef: MutableState<dynamic>,
    containerId: String,
    root: dynamic,
    selectedRoom: Room?,
    unitSize: Int,
    copyFieldName: String,
    deleteFieldName: String,
    onFurnitureFocus: (Furniture?) -> Unit,
    updateFurniture: (String, Int, Int, Int, Int, Int) -> Unit,
    transformFurniture: (String, Int, Int, Int, Int, Int, String?) -> Unit,
    onFurnitureCopy: (Furniture) -> Unit,
    onFurnitureDelete: (String) -> Unit,
) {
    val Stage = Konva.Stage
    val Layer = Konva.Layer
    val KonvaImage = Konva.Image

    val loadedImages = mutableMapOf<String, HTMLImageElement>()
    var loadedCount = 0
    val totalImages = selectedRoom?.furniture?.size ?: 0

    fun getOrCreateGridLayer(): dynamic {
        if (persistentGridLayer == null || lastUnitSize != unitSize) {
            lastUnitSize = unitSize
            persistentGridLayer = React.createElement(
                Layer,
                jsObject<Any> {
                    key = "grid-layer"
                    perfectDrawEnabled = false
                    listening = true
                },
                buildList {
                    val gridSize = unitSize * 2
                    val cols = 1352 / gridSize
                    val rows = 804 / gridSize

                    for (col in 0 until cols) {
                        for (row in 0 until rows) {
                            add(React.createElement(
                                Konva.Rect,
                                jsObject<Any> {
                                    x = col * gridSize.toDouble()
                                    y = row * gridSize.toDouble()
                                    width = gridSize.toDouble()
                                    height = gridSize.toDouble()
                                    stroke = "#e0e0e0"
                                    strokeWidth = 1
                                    fill = "#ffffff"
                                    listening = false
                                    perfectDrawEnabled = false
                                }
                            ))
                        }
                    }
                }.toTypedArray()
            )
        }
        return persistentGridLayer
    }

    fun renderStage() {
        document.getElementById("konva-context-menu")?.remove()

        var selectedNode: dynamic = null
        var contextMenuNode: dynamic = null

        val contextMenu = document.createElement("div").apply {
            setAttribute("id", "konva-context-menu")
            setAttribute("style", """
            position: fixed;
            display: none;
            background-color: #fff;
            border: 1px solid #ccc;
            padding: 8px 0;
            box-shadow: 0 2px 5px rgba(0,0,0,0.2);
            border-radius: 4px;
            z-index: 9999;
            min-width: 120px;
        """.trimIndent())
        }

        val menuItemStyle = """
            padding: 8px 16px;
            cursor: pointer;
            background-color: #fff;
            transition: background-color 0.2s;
            font-family: Plus Jakarta Sans;
            font-size: 14px;
            color: #333;
            user-select: none;
        """.trimIndent()

        val menuItemHoverStyle = """
            padding: 8px 16px;
            cursor: pointer;
            background-color: #f5f5f5;
            transition: background-color 0.2s;
            font-family: Plus Jakarta Sans;
            font-size: 14px;
            color: #333;
            user-select: none;
        """.trimIndent()

        val copyItem = document.createElement("div").apply {
            setAttribute("id", copyFieldName)
            textContent = "Copy"
            setAttribute("style", menuItemStyle)
        }

        val deleteItem = document.createElement("div").apply {
            setAttribute("id", deleteFieldName)
            textContent = "Delete"
            setAttribute("style", menuItemStyle)
        }

        copyItem.addEventListener("mouseover",
            { copyItem.setAttribute("style", menuItemHoverStyle) })
        copyItem.addEventListener("mouseout",
            { copyItem.setAttribute("style", menuItemStyle) })
        deleteItem.addEventListener("mouseover",
            { deleteItem.setAttribute("style", menuItemHoverStyle) })
        deleteItem.addEventListener("mouseout",
            { deleteItem.setAttribute("style", menuItemStyle) })

        contextMenu.appendChild(copyItem)
        contextMenu.appendChild(deleteItem)
        document.body?.appendChild(contextMenu)

        fun hideContextMenu() {
            contextMenu.setAttribute("style", """
            position: fixed;
            display: none;
            background-color: #fff;
            border: 1px solid #ccc;
            padding: 8px 0;
            box-shadow: 0 2px 5px rgba(0,0,0,0.2);
            border-radius: 4px;
            z-index: 9999;
            min-width: 120px;
        """.trimIndent())
            contextMenuNode = null
        }

        val documentClickHandler = { _: dynamic -> hideContextMenu() }
        document.addEventListener("click", documentClickHandler)

        var activeNodeId: String? = null


        copyItem.addEventListener("click", { e: dynamic ->
            e.stopPropagation()
            activeNodeId?.let { id ->
                val furniture = selectedRoom?.furniture?.find { it?.id == id }
                if (furniture != null) {
                    onFurnitureCopy(furniture)
                }
            }
            hideContextMenu()
        })

        deleteItem.addEventListener("click", { e: dynamic ->
            e.stopPropagation()
            activeNodeId?.let { id ->
                onFurnitureDelete(id)
                activeNodeId = null
            }
            hideContextMenu()
        })


        val furnitureElements = selectedRoom?.furniture?.mapNotNull { furniture ->
            val furnitureId = furniture?.id ?: return@mapNotNull null


            val loadedImage = loadedImages[furnitureId]

            if (loadedImage == null) {
                console.warn("No loaded image for furniture:", furnitureId)
                return@mapNotNull null
            }

            val group = React.createElement(
                Konva.Group,
                jsObject<Any> {
                    key = furnitureId
                    x = if (furniture.shape=="round" && furniture.objectKind=="table"){
                        furniture.positionLeft!!-(furniture.width!!/2.0)
                    }else
                        furniture.positionLeft

                    y = if (furniture.shape=="round" && furniture.objectKind=="table"){
                        furniture.positionTop!! -(furniture.width!!/2.0)
                    }else
                        furniture.positionTop
                    width = furniture.width
                    height = furniture.height
                    rotation = if(
                            furniture.objectKind=="table"||
                            furniture.objectKind=="tree"||
                            furniture.objectKind=="bush"||
                            furniture.objectKind=="plant"||
                            furniture.objectKind=="wc"||
                            furniture.objectKind=="bar"||
                            furniture.objectKind=="exit"
                        ) 0 else{
                            furniture.rotationDeg
                        }
                    draggable = true
                    transformsEnabled = "all"
                    listening = true
                    hitStrokeWidth = 0
                    name = "furniture-group"
                    onClick = { e: dynamic ->
                        e.cancelBubble = true
                        selectedNode = e.currentTarget
                        val layer = selectedNode.getLayer()
                        if (layer != null) {
                            val transformer = layer.findOne(".transformer")
                            if (furniture.objectKind=="wc"||
                                furniture.objectKind=="exit"
                            )
                            {
                                transformer?.enabledAnchors()
                                transformer?.resizeEnabled(false)
                                transformer?.keepRatio(false)
                            }
                            else if ((furniture.objectKind=="wall"
                                || furniture.objectKind=="window")
                                && furniture.height!! > furniture.width!!
                            )
                            {
                                transformer?.enabledAnchors(arrayOf(
                                    "top-center",
                                    "bottom-center",
                                ))
                                transformer?.resizeEnabled(true)
                                transformer?.keepRatio(false)

                            } else if(
                                (furniture.objectKind=="wall"
                                || furniture.objectKind=="window")
                                && furniture.height!! <= furniture.width!!
                            )
                            {
                                transformer?.enabledAnchors(arrayOf(
                                    "middle-left",
                                    "middle-right"
                                ))
                                transformer?.resizeEnabled(true)
                                transformer?.keepRatio(false)
                            }
                            else if (furniture.shape == "round"
                                || furniture.objectKind=="tree"
                                || furniture.objectKind=="bush"
                                || furniture.objectKind=="plant"
                                || furniture.objectKind=="chair"
                                || furniture.objectKind=="barChair"
                                || (
                                        furniture.objectKind=="column"
                                        && furniture.shape=="circle"
                                    )
                            ) {
                                transformer?.enabledAnchors(arrayOf(
                                    "top-left",
                                    "top-right",
                                    "bottom-left",
                                    "bottom-right"
                                ))
                                transformer?.resizeEnabled(true)
                                transformer?.keepRatio(true)
                            }
                            else {
                                transformer?.enabledAnchors(arrayOf(
                                    "top-left",
                                    "top-center",
                                    "top-right",
                                    "middle-left",
                                    "middle-right",
                                    "bottom-left",
                                    "bottom-center",
                                    "bottom-right"
                                ))
                                transformer?.resizeEnabled(true)
                                transformer?.keepRatio(false)
                            }
                            transformer?.nodes(arrayOf<dynamic>(e.currentTarget))
                            layer.batchDraw()
                        }
                        onFurnitureFocus(furniture)
                    }

                    onContextMenu = { e: dynamic ->
                        e.evt.preventDefault()
                        e.evt.stopPropagation()
                        contextMenuNode = e.target
                        activeNodeId = furnitureId

                        contextMenu.setAttribute("style", """
                            position: fixed;
                            display: block;
                            background-color: #fff;
                            border: 1px solid #ccc;
                            padding: 8px 0;
                            box-shadow: 0 2px 5px rgba(0,0,0,0.2);
                            border-radius: 4px;
                            z-index: 9999;
                            min-width: 120px;
                            left: ${e.evt.clientX}px;
                            top: ${e.evt.clientY}px;
                        """.trimIndent())
                    }

                    dragBoundFunc = { pos: dynamic ->
                        jsObject<Any> {
                            x = pos.x.unsafeCast<Double>()
                            y = pos.y.unsafeCast<Double>()
                        }
                    }

                    onDragMove = { e: dynamic -> e.cancelBubble = true }

                    onDragEnd = { e: dynamic ->
                        e.cancelBubble = true
                        val node = e.target
                        val layer = node.getLayer()
                        if (layer == null) {
                            console.error("Layer not found on drag end for node:", node)
                        }

                        val (magnetX, magnetY) = findNearestObjects(
                            node, layer, unitSize/4)
                        val finalX = if (magnetX == node.x()) snapToGrid(node.x(),
                            unitSize) else magnetX
                        val finalY = if (magnetY == node.y()) snapToGrid(node.y(),
                            unitSize) else magnetY

                        node.to(jsObject<Any> {
                            duration = 0.1
                            x = finalX
                            y = finalY
                            onFinish = {
                                if (furniture.shape=="round"){
                                    updateFurniture(
                                        furnitureId,
                                        finalX.toInt()+furniture.width!!/2,
                                        finalY.toInt() +furniture.height!!/2,
                                        node.rotation().unsafeCast<Double>().toInt(),
                                        node.width().unsafeCast<Double>().toInt()
                                            ,
                                        node.height().unsafeCast<Double>().toInt()

                                    )
                                }else{
                                    updateFurniture(
                                        furnitureId,
                                        finalX.toInt(),
                                        finalY.toInt(),
                                        node.rotation().unsafeCast<Double>().toInt(),
                                        node.width().unsafeCast<Double>().toInt(),
                                        node.height().unsafeCast<Double>().toInt()
                                    )
                                }

                            }
                        })
                    }

                    onTransform = { e: dynamic ->
                        e.cancelBubble = true
                        val node = e.target
                        val layer = node.getLayer()

                        val scaleX = node.scaleX().unsafeCast<Double>()
                        val scaleY = node.scaleY().unsafeCast<Double>()

                        var newWidth = node.width().unsafeCast<Double>() * scaleX
                        var newHeight = node.height().unsafeCast<Double>() * scaleY

                        if (newWidth < unitSize) newWidth = unitSize.toDouble()
                        if (newHeight < unitSize) newHeight = unitSize.toDouble()

                        node.width(newWidth)
                        node.height(newHeight)
                        node.scaleX(1.0)
                        node.scaleY(1.0)

                       if ( furniture.objectKind == "bar")
                       {
                            val backgroundNode = node.findOne(".furniture-background")
                            backgroundNode?.width(if (newWidth>40.0) newWidth else 40.0)
                            backgroundNode?.height(if(newHeight>40.0) newHeight else 40.0)
                            backgroundNode?.x(-(newWidth/2)+20.0)
                            backgroundNode?.y(-(newHeight/2)+20.0)
                            backgroundNode?.x(0)
                            backgroundNode?.y(0)

                            val imageNode = node.findOne(".furniture-image")
                            if (imageNode != null) {
                                imageNode.width(if (newWidth>40.0) newWidth else 40.0)
                                imageNode.height(if(newHeight>40.0) newHeight else 40.0)
                                imageNode.scaleX(1.0)
                                imageNode.scaleY(1.0)
                                imageNode.x(0)
                                imageNode.y(0)
                            }

                        }
                       else if (furniture.objectKind=="table"
                           && furniture.shape=="rectangle") {
                           val rectNode = node.findOne(".furniture-rect")
                           rectNode?.width(newWidth)
                           rectNode?.height(newHeight)
                           rectNode?.scaleX(1.0)
                           rectNode?.scaleY(1.0)

                           val textNode = node.findOne(".furniture-text")
                           if (textNode != null) {
                               textNode.width(newWidth)
                               textNode.height(newHeight)
                               textNode.fontSize(14)
                           }
                       }
                       else if (furniture.objectKind=="table"
                           && furniture.shape=="round"
                       ) {
                           val circleNode = node.findOne(".furniture-circle")
                           if (circleNode != null) {
                               val newRadius =newWidth/2
                               circleNode.radius(newRadius)
                               circleNode.x(newWidth/2)
                               circleNode.y(newWidth/2)
                           }

                           val textNode = node.findOne(".furniture-text")
                           if (textNode != null) {
                               textNode.width(newWidth)
                               textNode.height(newHeight)
                               textNode.fontSize(14)
                           }
                       }
                        else if (furniture.objectKind=="column"
                        ){
                           val columnNode = node.findOne(".furniture-column")
                           if (furniture.shape=="circle"){
                               columnNode.radius(newWidth/2)
                           }
                           columnNode?.width(newWidth)
                           columnNode?.height(newHeight)
                        }else if (furniture.objectKind=="window"
                           || furniture.objectKind=="wall"){
                           val imageNode = node.findOne(".furniture-window-wall")
                           imageNode?.width(newWidth)
                           imageNode?.height(newHeight)
                       }
                        else {
                            val imageNode = node.findOne(".furniture-image")
                            imageNode?.width(newWidth)
                            imageNode?.height(newHeight)

                            val textNode = node.findOne(".furniture-text")
                            if (textNode != null) {
                                textNode.width(newWidth)
                                textNode.height(newHeight)
                                textNode.fontSize(14)
                            }
                        }

                        layer?.batchDraw()
                    }

                    onTransformEnd = { e: dynamic ->
                        e.cancelBubble = true
                        val node = e.target

                        val snapX = snapToGrid(node.x(), unitSize)
                        val snapY = snapToGrid(node.y(), unitSize)

                        val currentTransformedWidth = node.width().unsafeCast<Double>()
                        val currentTransformedHeight = node.height().unsafeCast<Double>()

                        val snapWidth = snapToGrid(currentTransformedWidth, unitSize)
                        val snapHeight = snapToGrid(currentTransformedHeight, unitSize)
                        val rotationToSend = node.rotation().unsafeCast<Double>().toInt()

                        node.to(jsObject<Any>  {
                            duration = 0.1
                            x = snapX
                            y = snapY
                            width = snapWidth
                            height = snapHeight
                            onFinish = {
                                val finalAnimatedWidth = node.width().unsafeCast<Double>()
                                val finalAnimatedHeight = node.height().unsafeCast<Double>()

                                if (furniture.objectKind == "bar")
                                {
                                    val backgroundNode = node.findOne(".furniture-background")
                                    backgroundNode?.width(finalAnimatedWidth)
                                    backgroundNode?.height(finalAnimatedHeight)
                                    backgroundNode?.x(-(finalAnimatedWidth/2)+20)
                                    backgroundNode?.y(-(finalAnimatedHeight/2)+20)
                                    backgroundNode?.x(0)
                                    backgroundNode?.y(0)

                                    val imageNode = node.findOne(".furniture-image")
                                    if (imageNode != null) {
                                        imageNode.width(40.0)
                                        imageNode.height(40.0)
//                                        imageNode.width(finalAnimatedWidth)
//                                        imageNode.height(finalAnimatedHeight)
                                        imageNode.scaleX(1.0)
                                        imageNode.scaleY(1.0)
                                    }

                                }
                                else if ((furniture.objectKind=="wall"
                                            || furniture.objectKind=="window")
                                    && furniture.height!! > furniture.width!!
                                )
                                {
                                    val imageNode = node.findOne(".furniture-window-wall")
                                    imageNode?.width(finalAnimatedWidth)
                                    imageNode?.height(
                                        if (finalAnimatedHeight>60.0)
                                            finalAnimatedHeight else 60.0
                                    )
                                }
                                else if ((furniture.objectKind=="wall"
                                        || furniture.objectKind=="window")
                                        && furniture.height!! <= furniture.width!!
                                ){
                                    val imageNode = node.findOne(".furniture-window-wall")
                                    imageNode?.width(
                                        if (finalAnimatedWidth>60.0) finalAnimatedWidth
                                            else 60.0
                                    )
                                    imageNode?.height(finalAnimatedHeight)
                                }
                                else if (furniture.objectKind=="table"
                                    && furniture.shape=="rectangle")
                                {
                                    val imageNode = node.findOne(".furniture-rect")
                                    imageNode?.width(
                                        if (finalAnimatedWidth>40.0) finalAnimatedWidth
                                            else 40.0
                                    )
                                    imageNode?.height(
                                        if (finalAnimatedHeight>40.0) finalAnimatedHeight
                                            else 40.0
                                    )

                                    val textNode = node.findOne(".furniture-text")
                                    if (textNode != null) {
                                        textNode.width(finalAnimatedWidth)
                                        textNode.height(finalAnimatedHeight)
                                        textNode.fontSize(14)
                                        textNode.x(0)
                                        textNode.y(0)
                                    }
                                }else if (
                                    furniture.objectKind=="table"
                                    && furniture.shape=="round"
                                ){
                                    val imageNode = node.findOne(".furniture-circle")
                                    imageNode.radius(finalAnimatedWidth/2)

                                    imageNode?.width(
                                        if (finalAnimatedWidth>40.0) finalAnimatedWidth
                                        else 40.0
                                    )
                                    imageNode?.height(
                                        if (finalAnimatedHeight>40.0) finalAnimatedHeight
                                        else 40.0
                                    )

                                    val textNode = node.findOne(".furniture-text")
                                    if (textNode != null) {
                                        textNode.width(finalAnimatedWidth)
                                        textNode.height(finalAnimatedHeight)
                                        textNode.fontSize(14)
                                        textNode.x(0)
                                        textNode.y(0)
                                    }
                                }
                                else if (furniture.objectKind=="column")
                                {
                                    val imageNode = node.findOne(".furniture-column")
                                    if (furniture.shape=="circle"){
                                        imageNode.radius(finalAnimatedWidth/2)
                                    }

                                    imageNode?.width(finalAnimatedWidth )
                                    imageNode?.height(finalAnimatedHeight)
                                }
                                else {
                                    imageNode?.width(
                                        if (finalAnimatedWidth>40.0) finalAnimatedWidth
                                        else 40.0
                                    )
                                    imageNode?.height(
                                        if (finalAnimatedHeight>40.0) finalAnimatedHeight
                                        else 40.0
                                    )

                                    val textNode = node.findOne(".furniture-text")
                                    if (textNode != null) {
                                        textNode.width(finalAnimatedWidth)
                                        textNode.height(finalAnimatedHeight)
                                        textNode.fontSize(14)
                                        textNode.x(0)
                                        textNode.y(0)
                                    }
                                }
                                node.getLayer()?.batchDraw()
                            }
                        })

                        if (furniture.objectKind=="table" && furniture.shape=="round")
                        {
                            val centerX = snapX + snapWidth/2
                            val centerY = snapY + snapHeight/2

                            transformFurniture(
                                furnitureId,
                                centerX.toInt(),
                                centerY.toInt(),
                                rotationToSend,
                                snapWidth.toInt(),
                                snapHeight.toInt(),
                                furniture.objectKind
                            )
                        }else{
                            transformFurniture(
                                furnitureId,
                                snapX.toInt(),
                                snapY.toInt(),
                                rotationToSend,
                                snapWidth.toInt(),
                                snapHeight.toInt(),
                                furniture.objectKind
                            )
                        }
                    }

                }
                ,
                buildList {

                    if (furniture.objectKind==ObjectKinds.BAR.value)
                    {
                        addAll(arrayOf(
                            React.createElement(
                                Konva.Rect,
                                jsObject<Any> {
                                    key = "$furnitureId-background"
                                    width = if (furniture.width!!>40.0)
                                        furniture.width else 40.0
                                    height = if (furniture.height!!>40.0)
                                        furniture.height else 40.0

                                    x=-(furniture.width/2)+20.0
                                    y=-(furniture.height/2)+20.0
                                    fill = "#ffffff"
                                    stroke = "#434343"
                                    strokeWidth = 1
                                    name = "furniture-background"
                                }
                            ),
                            React.createElement(
                                KonvaImage,
                                jsObject<Any> {
                                    key = "$furnitureId-image"
                                    width = 40.0
                                    height = 40.0
                                    x = 0
                                    y = 0
                                    image = loadedImage
                                    perfectDrawEnabled = false
                                    strokeScaleEnabled = false
                                    shadowForStrokeEnabled = false
                                    name = "furniture-image"
                                    transformsEnabled = "none"
                                    draggable = false
                                }
                            )
                        ))
                    }
                   else if (
                       furniture.objectKind == ObjectKinds.WC.value
                       ||furniture.objectKind == ObjectKinds.EXIT.value
                   )
                   {
                        addAll(arrayOf(
//                            React.createElement(
//                                Konva.Rect,
//                                jsObject<Any> {
//                                    key = "$furnitureId-background"
//                                    width = if (furniture.width!!>40.0)
//                                        furniture.width else 40.0
//                                    height = if (furniture.height!!>40.0)
//                                        furniture.height else 40.0
//
//                                    x=-(furniture.width/2)+20.0
//                                    y=-(furniture.height/2)+20.0
//                                    fill = "#ffffff"
//                                    stroke = "#434343"
//                                    strokeWidth = 1
//                                    name = "furniture-background"
//                                }
//                            ),
                            React.createElement(
                                KonvaImage,
                                jsObject<Any> {
                                    key = "$furnitureId-image"
                                    width = 40.0
                                    height = 40.0
                                    x = 0
                                    y = 0
                                    image = loadedImage
                                    perfectDrawEnabled = false
                                    strokeScaleEnabled = false
                                    shadowForStrokeEnabled = false
                                    name = "furniture-image"
                                    transformsEnabled = "none"
                                    draggable = false
                                }
                            )
                        ))
                    }
                    else if (
                        furniture.objectKind==ObjectKinds.TABLE_ROUND.value
                        && furniture.shape==ObjectKinds.TABLE_ROUND.shape?.value
                    ){
                        add(React.createElement(
                            Konva.Circle,
                            jsObject<Any> {
                                key = "$furnitureId-circle"
                                x = furniture.width!! / 2
                                y = furniture.height!! / 2
                                radius = furniture.width / 2
                                stroke = "#434343"
                                strokeWidth = 2
                                fill = furniture.componentColor?:"#FFFFFF"
                                name = "furniture-circle"
                            }
                        ))

                        add(React.createElement(
                            Konva.Text,
                            jsObject<Any> {
                                key = "$furnitureId-text"
                                x = 0
                                y = 0
                                width = furniture.width
                                height = furniture.height
                                text = furniture.caption ?: ""
                                fontSize = 14
                                fontFamily = "Plus Jakarta Sans"
                                fill = "#000000"
                                align = "center"
                                verticalAlign = "middle"
                                listening = false
                                name = "furniture-text"
                                padding = 5
                            }
                        ))


                    }
                    else if (
                        furniture.objectKind==ObjectKinds.TABLE_RECTANGLE.value
                        && furniture.shape==ObjectKinds.TABLE_RECTANGLE.shape?.value
                    ){
                        add(React.createElement(
                            Konva.Rect,
                            jsObject<Any> {
                                key = "$furnitureId-rect"
                                x = 0
                                y = 0
                                width = furniture.width
                                height = furniture.height
                                stroke = "#434343"
                                fill = furniture.componentColor?:"#FFFFFF"
                                strokeWidth = 2
                                cornerRadius=5
                                name = "furniture-rect"
                            }
                        ))


                        add(React.createElement(
                            Konva.Text,
                            jsObject<Any> {
                                key = "$furnitureId-text"
                                x = 0
                                y = 0
                                width = furniture.width
                                height = furniture.height
                                text = furniture.caption ?: ""
                                fontSize = 14
                                fontFamily = "Plus Jakarta Sans"
                                fill = "#000000"
                                align = "center"
                                verticalAlign = "middle"
                                listening = false
                                name = "furniture-text"
                                padding = 5
                            }
                        ))
                    }
                   else if (
                       furniture.objectKind==ObjectKinds.ROUNDED_COLUMN.value
                       && furniture.shape==ObjectKinds.ROUNDED_COLUMN.shape?.value
                   ){

                       add(React.createElement(
                           Konva.Circle,
                           jsObject<Any> {
                               key = "$furnitureId-column"
                               x = 0
                               y = 0
                               radius = furniture.width!! / 2
                               stroke = "#434343"
                               strokeWidth = 2
                               name = "furniture-column"
                               fillPatternImage = createDiagonalStripes(
                                   "#DDDDDD",
                                   "#FFFFFF"
                               )
                               fillPatternRepeat = "repeat"
                               fillPatternRotation = 90
                               fillPatternScale = jsObject<Any> {
                                   x = 0.5
                                   y = 0.5
                               }
                           }
                       ))

                   }
                   else if (
                       furniture.objectKind==ObjectKinds.SQAURED_COLUMN.value
                       && furniture.shape==ObjectKinds.SQAURED_COLUMN.shape?.value
                   )
                   {
                       add(React.createElement(
                           Konva.Rect,
                           jsObject<Any> {
                               key = "$furnitureId-column"
                               x = 0
                               y = 0
                               width = furniture.width
                               height = furniture.height
                               stroke = "#434343"
                               strokeWidth = 2
                               cornerRadius=5
                               name = "furniture-column"
                               fillPatternImage = createDiagonalStripes(
                                   "#DDDDDD",
                                   "#FFFFFF"
                               )
                               fillPatternRepeat = "repeat"
                               fillPatternRotation = 90
                               fillPatternScale = jsObject<Any> {
                                   x = 0.5
                                   y = 0.5
                               }
                           }
                       ))
                   }
                   else if (furniture.objectKind==ObjectKinds.VERTICAL_WALL.value){
                       add(React.createElement(
                           Konva.Rect,
                           jsObject<Any> {
                               key = "$furnitureId-window-wall"
                               x = 0
                               y = 0
                               width = furniture.width
                               height = furniture.height
                               stroke = "#434343"
                               strokeWidth = 2
                               name = "furniture-window-wall"
                               fillPatternImage = createDiagonalStripes(
                                   "#DDDDDD",
                                   "#FFFFFF"
                               )
                               fillPatternRepeat = "repeat"
                               fillPatternRotation = 90
                               fillPatternScale = jsObject<Any> {
                                   x = 0.5
                                   y = 0.5
                               }
                           }
                       ))

                   }else if (furniture.objectKind==ObjectKinds.VERTICAL_WINDOW.value){
                       add(React.createElement(
                           Konva.Rect,
                           jsObject<Any> {
                               key = "$furnitureId-window-wall"
                               x = 0
                               y = 0
                               width = furniture.width
                               height = furniture.height
                               fill = "#2196F314"
                               stroke = "#2196F3"
                               strokeWidth = 2
                               name = "furniture-window-wall"
                           }
                       ))
                   }
                    else {
                        add(React.createElement(
                            KonvaImage,
                            jsObject<Any> {
                                key = "$furnitureId-image"
                                width = furniture.width
                                height = furniture.height
                                image = loadedImage
                                perfectDrawEnabled = false
                                shadowForStrokeEnabled = false
                                strokeScaleEnabled = false
                                name = "furniture-image"
                                transformsEnabled = "all"
                            }
                        ))

                    }
                }.toTypedArray()
            )

            group
        } ?: emptyList()

        val transformer = React.createElement(
            Konva.Transformer,
            jsObject<Any> {
                name = "transformer"
                boundBoxFunc = { oldBox: dynamic, newBox: dynamic ->
                    if (newBox.width < unitSize || newBox.height < unitSize) oldBox
                    else {
                        jsObject<Any> {
                            x = newBox.x
                            y = newBox.y
                            width = newBox.width
                            height = newBox.height
                            rotation = newBox.rotation
                        }
                    }
                }
                rotationSnaps = arrayOf(0, 90, 180, 270)
                rotationSnapTolerance = 5
                enabledAnchors = arrayOf(
                    "top-left",
                    "top-center",
                    "top-right",
                    "middle-left",
                    "middle-right",
                    "bottom-left",
                    "bottom-center",
                    "bottom-right"
                )
                resizeEnabled = true
                keepRatio = false
                borderStroke = "#0000FF"
                borderStrokeWidth = 2
                anchorStroke = "#0000FF"
                anchorFill = "#FFFFFF"
                padding=8
                anchorSize = 12
                centeredScaling = false

                onTransformStart = { e: dynamic ->
                    val node = e.target
                    val furniture = selectedRoom?.furniture
                        ?.find { it?.id == node.attrs.key }
                    if (furniture?.shape == "round"
                        || furniture?.objectKind=="wall"
                        || furniture?.objectKind=="window"
                        || furniture?.objectKind=="tree"
                        || furniture?.objectKind=="bush"
                        || furniture?.objectKind=="plant"
                        || furniture?.objectKind=="chair"
                        || furniture?.objectKind=="barChair"
                        || furniture?.objectKind=="column"
                    ) {
                        e.target.find(".transformer").forEach { transformer ->
                            transformer.enabledAnchors(emptyArray<String>())
                            transformer.resizeEnabled(false)
                        }
                    }
                }
            }
        )

        root.render(
            React.createElement(
                Stage,
                jsObject<Any> {
                    width = 1280.0
                    height = 900.0
                    perfectDrawEnabled = true
                    onClick = { e: dynamic ->
                        if (e.target == e.currentTarget) {
                            val stageNode = e.currentTarget
                            val layers = stageNode.getLayers()
                            if (layers != null && layers.length > 0) {
                                val layer = layers[1]
                                val transformerNode = layer.findOne(".transformer")
                                transformerNode?.nodes(emptyArray<dynamic>())
                                selectedNode = null
                                layer.batchDraw()
                                onFurnitureFocus(null)
                            }
                        }
                        hideContextMenu()
                    }
                },
                getOrCreateGridLayer(), // Use persistent grid layer
                React.createElement(
                    Layer,
                    jsObject<Any> {
                        key = "main-layer"
                        perfectDrawEnabled = false
                    },
                    buildList {
                        addAll(furnitureElements)
                        add(transformer)
                    }.toTypedArray()
                )
            )
        )
    }

    DisposableEffect(Unit) {
        val container = document.getElementById(containerId)
        if (rootRef.value == null) {
            rootRef.value = ReactDom.createRoot(container)
        }

        onDispose {
            rootRef.value?.unmount()
            rootRef.value = null
            persistentGridLayer = null
            lastUnitSize = -1
        }
    }

    DisposableEffect(selectedRoom?.id, selectedRoom?.furniture?.size, rootRef.value) {
        if (rootRef.value == null) {
            return@DisposableEffect onDispose {
                document.getElementById("konva-context-menu")?.remove()
            }
        }

        if (selectedRoom == null) {
            root.render(
                React.createElement(
                    Konva.Stage,
                    jsObject<Any> {
                        width = 1280.0
                        height = 900.0
                        perfectDrawEnabled = true
                    },
                    getOrCreateGridLayer()
                )
            )
            return@DisposableEffect onDispose {
                document.getElementById("konva-context-menu")?.remove()
            }
        }

        selectedRoom.furniture?.forEach { furniture ->
            val furnitureId = furniture?.id ?: return@forEach
            val svgPath = ObjectKinds.entries.find {
                it.value == furniture.objectKind &&
                        it.shape?.value == furniture.shape
            }?.img?.removeUrlWrapper()

            if (svgPath != null) {
                loadSvgAsImage(
                    svgPath = svgPath,
                    color = furniture.componentColor
                        ?: if (furniture.objectKind==ObjectKinds.TABLE_RECTANGLE.value){
                            Theme.White.hex
                        }else null

                ) { loadedImage ->
                    loadedImages[furnitureId] = loadedImage
                    loadedCount++
                    if (loadedCount == totalImages) renderStage()
                }
            } else {
                console.error("No SVG path found for furniture:", furnitureId)
                loadedCount++
                if (loadedCount == totalImages) renderStage()
            }
        }

        if (totalImages == 0) {
            renderStage()
        }

        onDispose {
            document.getElementById("konva-context-menu")?.remove()
        }
    }
}

private fun createDiagonalStripes(
    stripeColor: String,
    backgroundColor: String): HTMLCanvasElement {
    val canvas = document.createElement("canvas") as HTMLCanvasElement
    // Make canvas larger to accommodate thicker lines
    canvas.width = 40
    canvas.height = 40

    val ctx = canvas.getContext("2d") as CanvasRenderingContext2D

    // Fill background
    ctx.fillStyle = backgroundColor
    ctx.fillRect(0.0, 0.0, 40.0, 40.0)

    // Draw diagonal stripes
    ctx.fillStyle = stripeColor

    val thickness = 20.0
    val totalWidth = thickness * 2

    // Create diagonal stripes at 45 degrees
    for (i in -40 until 40 step totalWidth.toInt()) {
        ctx.beginPath()
        ctx.moveTo(i.toDouble(), 0.0)
        ctx.lineTo(i + 40.0, 40.0)
        ctx.lineTo(i + 40.0 + thickness, 40.0)
        ctx.lineTo(i + thickness, 0.0)
        ctx.closePath()
        ctx.fill()
    }

    return canvas
}


fun snapToGrid(value: Double, gridSize: Int): Double {
    return (value / gridSize).roundToInt() * gridSize.toDouble()
}

fun findNearestObjects(node: dynamic, layer: dynamic,
                       threshold: Int): Pair<Double, Double> {
    var snapX = node.x().unsafeCast<Double>()
    var snapY = node.y().unsafeCast<Double>()

    try {
        val children = layer?.children ?: return Pair(snapX, snapY)

        children.forEach { other ->
            if (other != null && other != node && other.className != "Transformer") {
                try {
                    val otherX = other.x()?.unsafeCast<Double>()
                    val otherY = other.y()?.unsafeCast<Double>()

                    if (otherX != null) {
                        val xDiff = abs(snapX - otherX)
                        if (xDiff < threshold) {
                            snapX = otherX
                        }
                    }

                    if (otherY != null) {
                        val yDiff = abs(snapY - otherY)
                        if (yDiff < threshold) {
                            snapY = otherY
                        }
                    }
                } catch (e: Exception) {
                    console.error("Error processing object:", e)
                }
            }
        }
    } catch (e: Exception) {
        console.error("Error in findNearestObjects:", e)
    }

    return Pair(snapX, snapY)
}

fun String.removeUrlWrapper(): String {
    return this.replace("url('", "").replace("')", "")
}

private fun loadSvgAsImage(
    svgPath: String,
    color: String?,
    onLoad: (HTMLImageElement) -> Unit
) {
    val xhr = XMLHttpRequest()
    xhr.open("GET", svgPath, true)
    xhr.onload = {
        if (xhr.status == 200.toShort()) {
            var svg = xhr.responseText

            svg = svg.replace("<svg",
                """<svg preserveAspectRatio="xMidYMid meet"""")

            if (!color.isNullOrBlank()) {
                svg = svg.replace("""fill="#[^"]*"""".toRegex(), """fill="$color"""")
                    .replace("""fill="black"""", """fill="$color"""")
                    .replace("""fill="white"""", """fill="$color"""")
                    .replace("""fill="none"""", """fill="$color"""")
            }

            val blob = Blob(arrayOf(svg), BlobPropertyBag(type = "image/svg+xml"))
            val url = URL.createObjectURL(blob)

            val img = document.createElement("img") as HTMLImageElement
            img.onload = {
                onLoad(img)
                URL.revokeObjectURL(url)
            }
            img.src = url
        }
    }
    xhr.send()
}